/**
 * PrizePop Strategy
 * Implements CampaignSectionStrategy for prize-pop behavior
 * Single Responsibility: Only handles prize-pop domain logic
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import PrizePopPanel from '../prizePop';
import CampaignGamesGrid from '../../MultiViewCampaignSection/components/CampaignGamesGrid';
import { CampaignSectionFallback } from '../../MultiViewCampaignSection/components';
import MultiCampaignsGrid from '../../defaults/MultiCampaignGrid/MultiCampaignGrid';

import {
  useItemsByCampaignType,
  PRIZE_POP_CAMPAIGN_TYPE,
} from 'features/campaigns';
import { useCampaignSectionBase } from '../../MultiViewCampaignSection/_hooks/useCampaignSection';
import { useCampaignPanelInfo } from '../../MultiViewCampaignSection/_hooks/useCampaignPanelInfo';
import type {
  CampaignSectionStrategy,
  CampaignSectionState,
} from '../../MultiViewCampaignSection/types';
import type ISectionSettings from 'types/models/ISectionSettings';
import type { CampaignSource } from '../../../types/campaign.types';

/** Hook implementation for prize-pop section state */
const usePrizePopSectionState = (
  data: ISectionSettings,
  isActive: boolean
): CampaignSectionState<CampaignSource> => {
  const { t } = useTranslation();
  const base = useCampaignSectionBase(data, isActive);

  const { items: campaigns } = useItemsByCampaignType(PRIZE_POP_CAMPAIGN_TYPE) as { items: CampaignSource[]; hasMany: boolean; hasItems: boolean };

  const isLoading = false;
  const isError = false;
  const error = null;

  const handleRetry = useCallback(() => {
    console.log('[PrizePopStrategy] Retry prize-pop');
  }, []);

  const isSingleItem = campaigns.length === 1;
  const activeCampaign = isSingleItem ? campaigns[0] : null;

  const containerProps = base.createContainerProps({
    'data-one-prizepop': isSingleItem ? 'true' : 'false',
  });

  const panelInfo = useCampaignPanelInfo({
    campaignType: PRIZE_POP_CAMPAIGN_TYPE,
    data,
    items: campaigns,
  });

  return {
    t,
    viewMode: base.viewMode,
    isLoading,
    isError,
    error,
    panelInfo,
    items: campaigns,
    isSingleItem,
    activeCampaign,
    handleViewToggle: base.handleViewToggle,
    handleRetry,
    handleNavigateToLobby: base.handleNavigateToLobby,
    containerProps,
    itemCount: campaigns.length,
  };
};

/** PrizePop Strategy Implementation */
export const prizePopStrategy: CampaignSectionStrategy<CampaignSource> = {
  type: PRIZE_POP_CAMPAIGN_TYPE,

  useCampaignSection: usePrizePopSectionState,

  renderMultiItemsLayout: ({ items, name, isActive }) => (
    <MultiCampaignsGrid name={name} campaigns={items} isActive={isActive} />
  ),

  // Single prize-pop layout - renders only the games grid (panel handled by CampaignSection)
  renderSingleItemLayout: ({ item, data, isActive }) => (
    <CampaignGamesGrid data={data} campaign={item} isActive={isActive} />
  ),

  // PrizePop-specific panel (multi view only) - uses store for data
  renderPanel: () => <PrizePopPanel />,

  renderFallback: ({ sectionName, error, onRetry, viewMode }) => (
    <CampaignSectionFallback
      sectionName={sectionName}
      error={error}
      onRetry={onRetry}
      viewMode={viewMode}
      sectionType="prize-pop"
    />
  ),
};
